<script>
  import { onDestroy, onMount } from "svelte";
  import { apiRequest } from "../utils/api";
  import { formatDate, formatDateOnly, toNumber, formatNumber, todayIsoDate } from "../utils/format.js";
  import RuleEditor from "./RuleEditor.svelte";
  import AgentRunPanel from "./AgentRunPanel.svelte";
  import RunArchiveView from "./RunArchiveView.svelte";

  export let isAdmin = false;
  export let selectedGenerationId = null;   // URL-owned, from AppShell
  export let selectedRunId = null;          // URL-owned run id (/runs/:runId), or null
  export let onOpen = () => {};             // onOpen(id): ask AppShell to open + push URL
  export let onClose = () => {};            // onClose(): ask AppShell to close + push URL
  export let onSelectRun = () => {};        // onSelectRun(runId|null): ask AppShell to push /runs/:runId
  export let onEditRules = () => {};        // onEditRules(): ask AppShell to push /generations/:id/rules

  let generations = [];
  let loadingGenerations = false;
  let generationsError = null;

  let selectedGeneration = null;
  let loadingSelectedGeneration = false;
  let selectedGenerationError = null;
  let loadedDetailId = null;

  let resetError = null;
  let resetMessage = null;
  let resetting = false;

  let templates = [];
  let loadingTemplates = false;

  let showCreateForm = false;
  let creatingGeneration = false;
  let createGenerationError = null;
  let generationForm = {
    name: "",
    project_template_id: "",
    target_balance: ""
  };

  let editMode = false;
  let editForm = {};
  let savingSettings = false;
  let settingsError = null;
  let settingsMessage = null;
  let importingTransactions = false;
  let importTransactionError = null;
  let importTransactionMessage = null;
  let importTransactionIssues = [];
  let importTransactionNotice = null;
  let importFileInput = null;
  let openPreviews = {};
  let screenshotBusy = {};
  let screenshotError = null;
  // Email-artifact UI state — mirrors the page screenshot/preview state above.
  let openEmailPreviews = {};
  let emailScreenshotBusy = {};
  let emailScreenshotError = null;

  // Layout-shell (Slice 1) UI state — pure presentation, no data flow.
  // Collapse state persists GLOBALLY per-browser (not per-generation) under a
  // single localStorage key, so it survives reloads and follows across
  // generations. Default = EXPANDED when absent (a fresh browser opens the panels
  // so the rule editor is reachable immediately). All storage access is guarded
  // so private-mode / disabled storage can never break the page.
  const PANEL_PREFS_KEY = "genPanels";

  function loadPanelPrefs() {
    try {
      const raw = window.localStorage.getItem(PANEL_PREFS_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  const panelPrefs = loadPanelPrefs();
  // Rules accordion defaults to expanded so its editor is reachable immediately.
  let rulesExpanded = panelPrefs.rules !== false;
  // Bottom transactions/rows block is collapsible; open by default.
  let transactionsExpanded = panelPrefs.transactions !== false;
  // Left run-history rail is collapsible too (parity with the other panels); open
  // by default so the run list is visible on first open.
  let runsExpanded = panelPrefs.runs !== false;

  function savePanelPrefs() {
    try {
      window.localStorage.setItem(
        PANEL_PREFS_KEY,
        JSON.stringify({ rules: rulesExpanded, transactions: transactionsExpanded, runs: runsExpanded })
      );
    } catch (_error) {
      // Storage unavailable (private mode / disabled) — the toggle still works
      // in-memory; we simply don't persist.
    }
  }

  // Run-history archive (Slice 2). The left rail lists every run for the open
  // generation; selecting one drives the center: the ACTIVE run → the live
  // AgentRunPanel, a TERMINAL run → the read-only RunArchiveView.
  const RUN_ACTIVE_STATUSES = new Set(["init", "queued", "running", "paused"]);
  let runs = [];
  // The DEFAULT (implicit) selection — active run if present, else the idle live
  // panel (null). This never rewrites the URL: /generations/:id stays clean until
  // the user EXPLICITLY clicks a run, at which point the URL-owned selectedRunId
  // prop takes over via effectiveRunId below.
  let defaultRunId = null;
  let runsRefreshTimer = null;
  // Slice 5: rule ids the live run is currently BLOCKED on, surfaced by the
  // AgentRunPanel from the timeline (latest verify's error_rules + latest post
  // rejection's rule_id). Drives the highlight in the Rules panel and the
  // collapsed-rail indicator. Cleared whenever the panel resets (gen switch).
  let flaggedRuleIds = [];
  function handleFlaggedRulesChange(ids) {
    flaggedRuleIds = Array.isArray(ids) ? ids : [];
  }
  // The highlight belongs to the LIVE panel; when the read-only archive is shown
  // (the panel is unmounted), clear any lingering flags so a past run's failure
  // doesn't keep lighting up the current Rules panel.
  $: if (showArchiveView) {
    flaggedRuleIds = [];
  }

  // The run the live AgentRunPanel currently owns (attached/started/hydrated). Its
  // own terminal state ("Run agent again") is shown by the panel, so selecting it
  // keeps the panel; only OTHER terminal runs open the read-only archive view.
  let panelRunId = null;

  // The effective selection: the URL-owned run id when it points at a run that
  // actually loaded (an explicit, shareable pick), otherwise the default. A stale
  // /runs/:id (not in the loaded runs) falls back to the default gracefully.
  $: urlRunIsLoaded = selectedRunId != null && runs.some((run) => run.id === selectedRunId);
  $: effectiveRunId = urlRunIsLoaded ? selectedRunId : defaultRunId;

  $: selectedRun = runs.find((run) => run.id === effectiveRunId) || null;
  // Show the read-only archive when the selected run is terminal AND it isn't the
  // panel's own run (the panel renders its own run — live or just-finished).
  $: showArchiveView = !!selectedRun &&
    !RUN_ACTIVE_STATUSES.has(selectedRun.status) &&
    selectedRun.id !== panelRunId;

  onMount(() => {
    loadGenerations();
    loadTemplates();
  });

  // Clear BOTH the run-rail refresh interval and any pending rows-refresh timer
  // on destroy — GenerationManager is unmounted whenever the user leaves the
  // generations section, so a debounced rows refresh armed just before must not
  // fire post-unmount (orphaned-timer class).
  onDestroy(resetRunHistory);

  // The URL-owned id flows in as a prop; load (or clear) the detail object in
  // response. The guard prevents double-loads and reactive loops.
  $: syncSelectedDetail(selectedGenerationId);
  function syncSelectedDetail(id) {
    if (id && id !== loadedDetailId) {
      loadedDetailId = id;
      resetError = null;
      resetMessage = null;
      // Abandons any in-flight re-render poll for the PREVIOUS generation (it checks
      // rerenderForId every tick) and takes its status/error off the header with it.
      clearRerenderState();
      resetRunHistory();
      loadGeneration(id);
      loadRuns(id, { selectDefault: true });
    } else if (!id && loadedDetailId !== null) {
      loadedDetailId = null;
      selectedGeneration = null;
      selectedGenerationError = null;
      resetError = null;
      resetMessage = null;
      clearRerenderState();
      resetRunHistory();
    }
  }

  function resetRunHistory() {
    stopRunsRefresh();
    if (rowsRefreshTimer) {
      clearTimeout(rowsRefreshTimer);
      rowsRefreshTimer = null;
    }
    rowsRefreshQueued = false;
    rowsRefreshInFlight = false;
    rowFlash = new Map();
    runs = [];
    defaultRunId = null;
    panelRunId = null;
  }

  // Fetch the full run history for a generation (active + terminal, newest
  // first). Default selection: the active run if present, else the most recent
  // run, else none (the idle "Run agent" state). A refetch never clobbers an
  // explicit user selection that still exists.
  async function loadRuns(generationId, { selectDefault = false } = {}) {
    if (!generationId) {
      return;
    }
    try {
      const fetched = await apiRequest(`/api/generations/${generationId}/agent_runs`);
      // Guard against a stale fetch after the user switched generations.
      if (generationId !== loadedDetailId) {
        return;
      }
      runs = fetched;

      const active = runs.find((run) => RUN_ACTIVE_STATUSES.has(run.status));
      // The DEFAULT selection: the ACTIVE run if one exists; otherwise the live
      // panel's own idle / "Run agent again" state (defaultRunId = null) rather
      // than auto-opening the read-only archive of the newest terminal run. This
      // never touches the URL — an explicit URL run (selectedRunId prop) overrides
      // it via effectiveRunId. The terminal runs stay listed and clickable in the
      // rail — just not auto-selected.
      if (selectDefault || defaultRunId === null) {
        defaultRunId = active?.id ?? null;
      } else if (!runs.some((run) => run.id === defaultRunId)) {
        // The previous default run vanished — fall back to active / idle.
        defaultRunId = active?.id ?? null;
      }

      manageRunsRefresh(active);
    } catch (_error) {
      // A run-history load failure is non-fatal: the rail simply stays empty and
      // the center falls back to the live panel's own idle state.
    }
  }

  // While a run is active, poll the history at a slow cadence so the rail badges
  // (and a newly-appearing run) stay fresh even without an explicit signal. When
  // nothing is active, stop polling entirely.
  function manageRunsRefresh(active) {
    if (active) {
      if (!runsRefreshTimer) {
        runsRefreshTimer = setInterval(() => loadRuns(loadedDetailId), 4000);
      }
    } else {
      stopRunsRefresh();
    }
  }

  function stopRunsRefresh() {
    if (runsRefreshTimer) {
      clearInterval(runsRefreshTimer);
      runsRefreshTimer = null;
    }
  }

  // Live transactions table (Slice 4). The live AgentRunPanel signals whenever a
  // row-mutating tool call (post_transaction / delete_transaction) completes; we
  // re-fetch the generation detail so selectedGeneration.generation_pages[].
  // generation_rows — and thus the bottom table + its summary — re-render with
  // the new rows, WITHOUT a manual page reload. loadGeneration only replaces the
  // detail object; it never touches run state, the run rail, or the panel.
  const ROWS_REFRESH_DEBOUNCE_MS = 500;
  let rowsRefreshTimer = null;
  let rowsRefreshInFlight = false;
  let rowsRefreshQueued = false;

  // ---- Live "what just changed" flash on the bottom transactions table ----
  // When a post/delete lands and the table re-fetches, pull the operator's eye to
  // exactly which row changed: a newly-posted row flashes green (+ scrolls into
  // view); a delete-tail flashes the surviving boundary row red. rowFlash maps a
  // row id to its flash kind while the CSS animation plays. Only fires when the
  // table is EXPANDED — a collapsed table has no rows in the DOM and just
  // refreshes silently.
  let txTableEl = null;
  let rowFlash = new Map();
  const ROW_FLASH_MS = 1600;

  function highlightRowDelta(prevRows, nextRows) {
    if (!transactionsExpanded) {
      return;
    }
    const prevIds = new Set(prevRows.map((row) => row.id));
    const nextIds = new Set(nextRows.map((row) => row.id));
    const added = nextRows.filter((row) => !prevIds.has(row.id));
    if (added.length > 0) {
      flashRows(added.map((row) => row.id), added[0].id, "add");
      return;
    }
    // No additions but rows vanished (a delete-tail): flash the new last row —
    // the boundary where the cut happened — so the removal is visible in place.
    const removed = prevRows.some((row) => !nextIds.has(row.id));
    if (removed && nextRows.length > 0) {
      const boundary = nextRows[nextRows.length - 1];
      flashRows([boundary.id], boundary.id, "remove");
    }
  }

  function flashRows(ids, scrollToId, kind) {
    const next = new Map(rowFlash);
    for (const id of ids) {
      next.set(id, kind);
    }
    rowFlash = next;
    // NO auto-scroll. Scrolling the changed row into view hijacked the operator's
    // scroll position on every insert (a UX regression) — a nice idea that read as
    // scroll-jacking. The flash alone signals the change: if the row is on-screen
    // the operator sees it; we never yank the viewport. (`scrollToId` is kept for
    // call-site compatibility.)
    setTimeout(() => {
      const cleared = new Map(rowFlash);
      for (const id of ids) {
        cleared.delete(id);
      }
      rowFlash = cleared;
    }, ROW_FLASH_MS);
  }

  // Debounced + non-overlapping detail refresh. A burst of posts collapses to a
  // single reload; a signal that lands while a reload is in flight queues exactly
  // one more so the final state is never missed.
  function handleRowsChanged() {
    if (!loadedDetailId) {
      return;
    }
    if (rowsRefreshTimer) {
      clearTimeout(rowsRefreshTimer);
    }
    rowsRefreshTimer = setTimeout(runRowsRefresh, ROWS_REFRESH_DEBOUNCE_MS);
  }

  async function runRowsRefresh() {
    rowsRefreshTimer = null;
    const target = loadedDetailId;
    if (!target) {
      return;
    }
    // Guard against overlapping in-flight reloads: coalesce into one trailing run.
    if (rowsRefreshInFlight) {
      rowsRefreshQueued = true;
      return;
    }
    rowsRefreshInFlight = true;
    // Snapshot the rows BEFORE the refetch so we can diff what the mutation
    // changed once the fresh detail lands (added → green, delete-tail → red).
    const prevRows = flattenRows(selectedGeneration);
    try {
      // SILENT refresh: unlike loadGeneration, this must NOT toggle
      // loadingSelectedGeneration — that flag swaps the whole gen shell (incl.
      // the live AgentRunPanel and run rail) for a skeleton, which would tear
      // down the run mid-poll. We only replace the detail object in place so the
      // bottom table + summary re-render while everything else stays mounted.
      const fresh = await apiRequest(`/api/generations/${target}`);
      // Drop a stale fetch if the user switched generations while it was in
      // flight, so we never clobber the newly-opened detail.
      if (loadedDetailId === target) {
        selectedGeneration = fresh;
        highlightRowDelta(prevRows, flattenRows(fresh));
      }
    } catch (_error) {
      // A live-refresh failure is non-fatal: keep the last-known table; the
      // terminal safety-net refresh (or the user's next reload) reconciles.
    } finally {
      rowsRefreshInFlight = false;
      if (rowsRefreshQueued) {
        rowsRefreshQueued = false;
        // Only chase the trailing refresh while still on the same generation.
        if (loadedDetailId === target) {
          runRowsRefresh();
        }
      }
    }
  }

  // The live panel signals every (runId, status) transition — refetch so a new
  // run appears in the rail and a terminal run gets its final badge/error. If
  // the user hasn't explicitly picked another run, follow the live one.
  const RUN_TERMINAL_STATUSES = new Set(["completed", "failed"]);
  function handleRunStatusChange(runId, status) {
    const isNewRun = runId !== panelRunId;
    panelRunId = runId;
    // Follow the panel's run in the rail unless the user has navigated to an
    // older archived run (a terminal run that isn't the panel's own).
    if (!showArchiveView) {
      defaultRunId = runId;
      // Make the live/attached run URL-ADDRESSABLE (shareable): reflect its id in
      // the address bar the first time we attach to it, so a shared link reopens
      // this exact run even once other runs exist. `replace` (not push) — this
      // mirrors panel state, it isn't a user navigation. Skipped when the user is
      // viewing another run's archive, and only on first attach (not every poll).
      if (isNewRun && selectedRunId !== runId) {
        onSelectRun(runId, { replace: true });
      }
    }
    loadRuns(loadedDetailId);
    // Terminal safety net: refresh the detail once when the run ends so the
    // bottom table shows the final row state even if the last mutation's
    // onRowsChanged signal raced the terminal transition.
    if (RUN_TERMINAL_STATUSES.has(status)) {
      handleRowsChanged();
    }
  }

  // A rail click is an EXPLICIT pick: ask AppShell to push /generations/:id/runs/:id
  // (or, for the panel's own run / "Current run", null → back to /generations/:id).
  // The URL then flows back as the selectedRunId prop.
  function selectRun(runId) {
    // Selecting the panel's own live/just-finished run keeps the clean URL — the
    // panel already renders it, so there's no archive to deep-link.
    onSelectRun(runId === panelRunId ? null : runId);
  }

  function runLabel(run) {
    return `Run #${run.id}`;
  }

  function runStatusBadgeClass(status) {
    if (status === "completed") {
      return "bg-success-bg text-success-text border-success-border/20";
    }
    if (status === "failed") {
      return "bg-danger-bg text-danger-text border-danger-border/20";
    }
    if (status === "paused") {
      return "bg-warning-bg text-warning-text border-warning-border/30";
    }
    if (status === "running" || status === "queued" || status === "init") {
      return "bg-brand-50 text-brand-600 border-brand-200/50 animate-pulse";
    }
    return "bg-neutral-100 text-neutral-600 border-neutral-200";
  }

  // "Relative created" — coarse buckets are enough for a run list.
  function relativeTime(value) {
    if (!value) {
      return "";
    }
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) {
      return "";
    }
    const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (seconds < 60) return "just now";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  }

  function togglePreview(pageId) {
    openPreviews = { ...openPreviews, [pageId]: !openPreviews[pageId] };
  }

  function pageDownloadUrl(page) {
    return `/api/generations/${selectedGeneration.id}/pages/${page.id}/download`;
  }

  function pngDownloadUrl(page) {
    return `${pageDownloadUrl(page)}?format=png`;
  }

  // "pending" is the initial (never-requested) state, so it reads as idle in the UI;
  // an in-flight render is tracked by screenshotBusy plus the "rendering" status.
  function isScreenshotInFlight(page) {
    return screenshotBusy[page.id] || page.screenshot_status === "rendering";
  }

  async function generateScreenshot(pageId) {
    if (!selectedGenerationId || screenshotBusy[pageId]) {
      return;
    }
    screenshotBusy = { ...screenshotBusy, [pageId]: true };
    screenshotError = null;
    try {
      await apiRequest(`/api/generations/${selectedGenerationId}/pages/${pageId}/screenshot`, {
        method: "POST"
      });
      await pollScreenshot(pageId);
    } catch (error) {
      screenshotError = error?.message || "Could not start screenshot.";
    } finally {
      screenshotBusy = { ...screenshotBusy, [pageId]: false };
    }
  }

  async function pollScreenshot(pageId, attempts = 0) {
    await refreshGenerationDetailSilently(selectedGenerationId);
    const page = selectedGeneration?.generation_pages?.find((candidate) => candidate.id === pageId);
    const status = page?.screenshot_status;
    if (status === "ready" || status === "failed" || attempts >= 40) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 3000));
    return pollScreenshot(pageId, attempts + 1);
  }

  // ---- Email artifacts (one per transaction row) — mirrors the page helpers ----
  function toggleEmailPreview(artifactId) {
    openEmailPreviews = { ...openEmailPreviews, [artifactId]: !openEmailPreviews[artifactId] };
  }

  function emailDownloadUrl(artifact) {
    return `/api/generations/${selectedGeneration.id}/email_artifacts/${artifact.id}/download`;
  }

  function emailPngDownloadUrl(artifact) {
    return `${emailDownloadUrl(artifact)}?format=png`;
  }

  // What the email IS: the spend it confirms. Every email in a generation goes to the
  // same customer and agent-posted rows have no transaction_id, so the old
  // transaction_id||email_address label printed the identical string on every entry.
  function emailLabel(artifact) {
    return formatDate(artifact.date_time);
  }

  function emailDescription(artifact) {
    return artifact.description || (rowIsTopUp(artifact) ? "Top-up" : `Email #${artifact.id}`);
  }

  // EUR is the figure shown in the email body itself; fall back to the project-currency
  // amount so a row missing the conversion still shows a number rather than a dash.
  // Returns the symbol with the number so a missing amount reads "—", not "€—".
  function emailAmount(artifact) {
    const eur = toNumber(artifact.amount_eur);
    if (eur !== null) {
      return `€${formatNumber(eur)}`;
    }

    const own = toNumber(artifact.amount);
    return own === null ? "—" : formatNumber(own);
  }

  function isEmailScreenshotInFlight(artifact) {
    return emailScreenshotBusy[artifact.id] || artifact.screenshot_status === "rendering";
  }

  async function generateEmailScreenshot(artifactId) {
    if (!selectedGenerationId || emailScreenshotBusy[artifactId]) {
      return;
    }
    emailScreenshotBusy = { ...emailScreenshotBusy, [artifactId]: true };
    emailScreenshotError = null;
    try {
      await apiRequest(`/api/generations/${selectedGenerationId}/email_artifacts/${artifactId}/screenshot`, {
        method: "POST"
      });
      await pollEmailScreenshot(artifactId);
    } catch (error) {
      emailScreenshotError = error?.message || "Could not start email screenshot.";
    } finally {
      emailScreenshotBusy = { ...emailScreenshotBusy, [artifactId]: false };
    }
  }

  async function pollEmailScreenshot(artifactId, attempts = 0) {
    await refreshGenerationDetailSilently(selectedGenerationId);
    const artifact = selectedGeneration?.email_artifacts?.find((candidate) => candidate.id === artifactId);
    const status = artifact?.screenshot_status;
    if (status === "ready" || status === "failed" || attempts >= 40) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 3000));
    return pollEmailScreenshot(artifactId, attempts + 1);
  }

  function generationStatusClass(status) {
    if (status === "completed") {
      return "bg-success-bg text-success-text border-success-border/20";
    }

    if (status === "preparation_error") {
      return "bg-danger-bg text-danger-text border-danger-border/20";
    }

    if (status === "in_progress") {
      return "bg-brand-50 text-brand-600 border-brand-200/50 animate-pulse";
    }

    return "bg-neutral-100 text-neutral-600 border-neutral-200";
  }

  function formatGenerationStatus(status) {
    return (status || "unknown").toString().replace(/_/g, " ");
  }

  const ROW_TYPE_TOP_UP = "top_up";
  const ROW_TYPE_SPENDING = "spending";

  function normalizedRowType(row) {
    if (row?.row_type === ROW_TYPE_TOP_UP || row?.row_type === 0 || row?.row_type === "0") {
      return ROW_TYPE_TOP_UP;
    }

    if (row?.row_type === ROW_TYPE_SPENDING || row?.row_type === 1 || row?.row_type === "1") {
      return ROW_TYPE_SPENDING;
    }

    return null;
  }

  function rowTypeBadgeClass(row) {
    if (rowIsTopUp(row)) {
      return "bg-info-bg/40 hover:bg-info-bg/60 text-neutral-900 transition-colors";
    }

    if (row.is_real === true) {
      return "bg-success-bg/30 hover:bg-success-bg/50 text-neutral-900 transition-colors";
    }

    return "bg-white hover:bg-neutral-50/50 text-neutral-900 transition-colors";
  }

  function rowTypeLabel(row) {
    if (rowIsTopUp(row)) {
      return "Top-up";
    }

    if (row.is_real === true) {
      return "Real spending";
    }

    return "MCP spending";
  }

  // Use helpers inside rows-table Svelte markup
  function rowIsTopUp(row) {
    return normalizedRowType(row) === ROW_TYPE_TOP_UP;
  }

  function rowIsSpending(row) {
    return normalizedRowType(row) === ROW_TYPE_SPENDING;
  }

  function flattenRows(generation) {
    if (!generation || !Array.isArray(generation.generation_pages)) {
      return [];
    }

    const rows = generation.generation_pages.flatMap((page) => {
      const orderedRows = Array.isArray(page.generation_rows)
        ? [ ...page.generation_rows ].sort((a, b) => (a.row_order || 0) - (b.row_order || 0))
        : [];

      return orderedRows.map((row) => ({
        ...row,
        page_number: page.page_number,
        page_id: page.id
      }));
    });

    return rows.sort((left, right) => {
      const pageCmp = (left.page_number || 0) - (right.page_number || 0);
      if (pageCmp !== 0) {
        return pageCmp;
      }

      const rowCmp = (left.row_order || 0) - (right.row_order || 0);
      if (rowCmp !== 0) {
        return rowCmp;
      }

      return (left.id || 0) - (right.id || 0);
    });
  }

  function generationSpendingRows(rows) {
    return rows.filter((row) => rowIsSpending(row));
  }

  // Sum total spent
  function sumSpendingAmount(rows) {
    return rows.reduce((sum, row) => {
      const amount = toNumber(row.amount);
      return sum + (amount || 0);
    }, 0);
  }

  function canResetGeneration(generation) {
    if (!generation) {
      return false;
    }

    return isAdmin || generation.status !== "completed";
  }

  async function loadGenerations() {
    loadingGenerations = true;
    generationsError = null;

    try {
      generations = await apiRequest("/api/generations");
      if (selectedGenerationId) {
        const stillExists = generations.find((generation) => generation.id === selectedGenerationId);
        if (!stillExists) {
          onClose();
        }
      }
    } catch (error) {
      generationsError = error?.message || "Could not load generations.";
      generations = [];
    } finally {
      loadingGenerations = false;
    }
  }

  async function loadGeneration(generationId) {
    if (!generationId) {
      return;
    }

    loadingSelectedGeneration = true;
    selectedGenerationError = null;

    try {
      selectedGeneration = await apiRequest(`/api/generations/${generationId}`);
    } catch (error) {
      selectedGenerationError = error?.message || "Could not load generation.";
      selectedGeneration = null;
    } finally {
      loadingSelectedGeneration = false;
    }
  }

  // Silent detail refetch: replaces the detail object IN PLACE without toggling
  // loadingSelectedGeneration. That flag swaps the whole gen shell for a skeleton
  // — used by the screenshot polls it made a PNG render flicker the entire panel
  // every 3s. Only the pages/emails whose screenshot_status changed need to
  // re-render; everything else stays mounted. Guarded against a generation
  // switch landing mid-fetch so a stale response can't clobber the new detail.
  async function refreshGenerationDetailSilently(generationId) {
    if (!generationId || selectedGenerationId !== generationId) {
      return;
    }
    try {
      const fresh = await apiRequest(`/api/generations/${generationId}`);
      if (selectedGenerationId === generationId) {
        selectedGeneration = fresh;
      }
    } catch (_error) {
      // Non-fatal: keep the last-known detail; the next poll tick or a manual
      // reload reconciles.
    }
  }

  // Roll back a completion (keep rows) and reopen the SAME agent loop so the model
  // keeps its full history. The status flip to draft re-attaches the run panel to
  // the reopened (paused) run, where the composer becomes a "Continue" action.
  let unconfirming = false;
  async function unconfirmGeneration() {
    if (!selectedGenerationId || unconfirming) {
      return;
    }
    if (!window.confirm(
      "Roll back this completion? The rows are kept and the agent run reopens with its full history so you can ask it to rework. Rendered artifacts stay but are marked stale until you complete again."
    )) {
      return;
    }
    unconfirming = true;
    resetError = null;
    try {
      await apiRequest(`/api/generations/${selectedGenerationId}/unconfirm`, { method: "POST" });
      await Promise.all([
        loadGeneration(selectedGenerationId),
        loadGenerations()
      ]);
    } catch (error) {
      resetError = error?.message || "Could not un-confirm the generation.";
    } finally {
      unconfirming = false;
    }
  }

  // "Re-render artifacts" — rebuild the PRESENTATION layer of a completed generation
  // (page HTML, email HTML, then a fresh PNG of each) from the CURRENT templates and
  // the CURRENT rows. Deliberately NOT the per-artifact "Re-generate" button, which
  // only re-shoots one PNG off the already persisted rendered_html; and deliberately
  // NOT Unconfirm, which reopens the agent run. Rows and status stay put.
  // ALL of this state is scoped to ONE generation id, not global. The poll below runs
  // for up to three minutes, and the user is free to select another generation while
  // it does — without the id these three would leak onto whatever card they open next
  // ("Re-rendering…" pinned to an unrelated draft, a red error from a different
  // generation). `rerenderForId` is the owner; syncSelectedDetail clears them on a
  // switch, and the template only shows them for the generation they belong to.
  let rerenderForId = null;
  let rerenderingArtifacts = false;
  let rerenderError = null;
  let rerenderMessage = null;

  // Tell the operator when the artifacts were last built, so they can judge whether a
  // re-render is even needed before firing one at the single render host.
  function rerenderTitle(generation) {
    const base =
      "Rebuild the page and email HTML from the current templates and rows, then re-shoot every PNG — the transactions, status and agent run are untouched";
    return generation?.artifacts_rendered_at
      ? `${base}. Last rendered: ${formatDate(generation.artifacts_rendered_at)}`
      : base;
  }

  function clearRerenderState() {
    rerenderForId = null;
    rerenderingArtifacts = false;
    rerenderError = null;
    rerenderMessage = null;
  }

  // A screenshot the batch is actually working on. `pending` alone is NOT a signal —
  // it doubles as the initial, never-requested state (see isScreenshotInFlight) — so
  // only count artifacts that HAVE rendered HTML, which are exactly the ones
  // ScreenshotJob.enqueue_all_for queues.
  function screenshotsInFlight(generation) {
    const artifacts = [
      ...(generation?.generation_pages || []),
      ...(generation?.email_artifacts || [])
    ];
    return artifacts.some(
      (artifact) =>
        artifact.rendered_html &&
        (artifact.screenshot_status === "pending" || artifact.screenshot_status === "rendering")
    );
  }

  function failedScreenshotCount(generation) {
    const artifacts = [
      ...(generation?.generation_pages || []),
      ...(generation?.email_artifacts || [])
    ];
    return artifacts.filter((artifact) => artifact.screenshot_status === "failed").length;
  }

  async function rerenderArtifacts() {
    if (!selectedGenerationId || rerenderingArtifacts) {
      return;
    }
    if (!window.confirm(
      "Re-render this generation's pages and emails from the CURRENT templates and rows? The existing HTML and PNGs are replaced. Transactions, status and the agent run are untouched."
    )) {
      return;
    }

    const generationId = selectedGenerationId;
    // The render stamp as it stands BEFORE the request — the job advancing it past
    // this value is the only trustworthy "the background render actually ran" signal.
    const renderedBefore = selectedGeneration?.artifacts_rendered_at || null;

    rerenderForId = generationId;
    rerenderingArtifacts = true;
    rerenderError = null;
    rerenderMessage = null;

    try {
      await apiRequest(`/api/generations/${generationId}/rerender_artifacts`, { method: "POST" });
      // Only claim it started once the server has ACCEPTED it.
      if (rerenderForId === generationId) {
        rerenderMessage = "Re-rendering artifacts…";
      }
      const outcome = await pollArtifactRerender(generationId, renderedBefore);
      // `abandoned` = the operator moved to another generation mid-poll. clearRerenderState
      // has already reset rerenderForId, but check the flag too: reporting an outcome we
      // never actually observed would be a lie either way.
      if (outcome.abandoned || rerenderForId !== generationId) {
        return;
      }
      if (outcome.timedOut) {
        rerenderMessage = "Still re-rendering — check the artifacts below.";
      } else if (outcome.failed > 0) {
        rerenderMessage = null;
        rerenderError = `Artifacts re-rendered, but ${outcome.failed} PNG render(s) failed.`;
      } else {
        rerenderMessage = "Artifacts re-rendered.";
      }
    } catch (error) {
      if (rerenderForId === generationId) {
        rerenderMessage = null;
        rerenderError = error?.message || "Could not re-render the artifacts.";
      }
    } finally {
      if (rerenderForId === generationId) {
        rerenderingArtifacts = false;
      }
    }
  }

  // The render runs in a background job, so nothing has changed the instant the POST
  // returns. Two phases, both polled off the same detail refetch so the rebuilt HTML
  // and each PNG appear live:
  //   1. wait for `artifacts_rendered_at` to move past its pre-request value — the job
  //      has run. Guessing from screenshot statuses instead would be wrong in both
  //      directions (`pending` is also the idle state; an unchanged template re-renders
  //      to byte-identical HTML).
  //   2. wait for the PNG batch to drain.
  // A shared attempt budget bounds the whole thing; running out stops the narration,
  // not the job, and says so rather than claiming success.
  const RERENDER_POLL_INTERVAL_MS = 3000;
  const RERENDER_POLL_ATTEMPTS = 60;

  async function pollArtifactRerender(generationId, renderedBefore) {
    let jobRan = false;

    for (let attempt = 0; attempt < RERENDER_POLL_ATTEMPTS; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, RERENDER_POLL_INTERVAL_MS));
      if (rerenderForId !== generationId) {
        return { timedOut: false, failed: 0, abandoned: true };
      }
      await refreshGenerationDetailSilently(generationId);
      if (rerenderForId !== generationId || selectedGenerationId !== generationId) {
        return { timedOut: false, failed: 0, abandoned: true };
      }

      const renderedAt = selectedGeneration?.artifacts_rendered_at || null;
      jobRan = jobRan || (renderedAt !== null && renderedAt !== renderedBefore);
      if (jobRan && !screenshotsInFlight(selectedGeneration)) {
        return { timedOut: false, failed: failedScreenshotCount(selectedGeneration), abandoned: false };
      }
    }

    return { timedOut: true, failed: failedScreenshotCount(selectedGeneration), abandoned: false };
  }

  async function resetGeneratedRows() {
    if (!selectedGeneration || !selectedGenerationId || resetting || !canResetGeneration(selectedGeneration)) {
      return;
    }

    const isCompletedGeneration = selectedGeneration.status === "completed";
    const confirmMessage = isCompletedGeneration
      ? "This completed generation will be reset and reopened. This deletes generated MCP rows starting from the first generated spending row, sets status to in_progress, and cannot be undone. Continue?"
      : "This will delete generated MCP rows starting from the first generated spending row. This action cannot be undone. Continue?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    resetting = true;
    resetError = null;
    resetMessage = null;

    try {
      const response = await apiRequest(`/api/generations/${selectedGenerationId}/reset_generated_rows`, {
        method: "POST"
      });

      resetMessage = response.message || "Generated rows reset.";
      if (typeof response.deleted === "number") {
        resetMessage = `${response.deleted} generated rows deleted. ${resetMessage}`;
      }

      await Promise.all([
        loadGeneration(selectedGenerationId),
        loadGenerations()
      ]);
    } catch (error) {
      resetError = error?.message || "Could not reset generated rows.";
    } finally {
      resetting = false;
    }
  }

  async function loadTemplates() {
    loadingTemplates = true;
    try {
      templates = await apiRequest("/api/templates");
    } catch (_error) {
      templates = [];
    } finally {
      loadingTemplates = false;
    }
  }

  function resetGenerationForm() {
    // Creation captures name + template + an OPTIONAL target balance (surfaced
    // here so it isn't forgotten in the draft's settings — a null target lets a
    // run complete with almost no spending). request_date/source_system are
    // required by the model, so we default them here; remaining settings
    // (customer name, data sources) stay in the draft settings form.
    generationForm = {
      name: "",
      project_template_id: "",
      target_balance: ""
    };
  }

  async function createGeneration(event) {
    event?.preventDefault();
    if (
      !generationForm.name.trim() ||
      !generationForm.project_template_id ||
      creatingGeneration
    ) {
      return;
    }

    createGenerationError = null;
    creatingGeneration = true;

    try {
      const generationBody = {
        name: generationForm.name.trim(),
        project_template_id: Number(generationForm.project_template_id),
        request_date: todayIsoDate(),
        source_system: "backoffice"
      };
      // Optional at creation: only send a target balance when the operator typed
      // one, so a blank field stays a NULL target (no forced spend-down). Guard on
      // the raw string FIRST — toNumber("") coerces to 0 (Number("") === 0), which
      // would silently persist target_balance = 0 (spend-to-zero) instead of null.
      const rawTarget = String(generationForm.target_balance ?? "").trim();
      if (rawTarget !== "") {
        const targetBalance = toNumber(rawTarget);
        if (targetBalance != null) {
          generationBody.target_balance = targetBalance;
        }
      }

      const response = await apiRequest("/api/generations", {
        method: "POST",
        body: { generation: generationBody }
      });

      resetGenerationForm();
      showCreateForm = false;
      await loadGenerations();
      if (response?.id) {
        onOpen(response.id);
      }
    } catch (error) {
      createGenerationError = error?.message || "Could not create generation.";
    } finally {
      creatingGeneration = false;
    }
  }

  function startEdit() {
    if (!selectedGeneration) {
      return;
    }
    settingsError = null;
    settingsMessage = null;
    editForm = {
      request_date: selectedGeneration.request_date
        ? String(selectedGeneration.request_date).slice(0, 10)
        : todayIsoDate(),
      target_balance: selectedGeneration.target_balance ?? 0,
      customer_name: selectedGeneration.customer_name ?? "",
      customer_email: selectedGeneration.customer_email ?? "",
      target_url: selectedGeneration.target_url ?? "",
      email_target_url: selectedGeneration.email_target_url ?? ""
    };
    editMode = true;
  }

  function cancelEdit() {
    editMode = false;
    editForm = {};
    settingsError = null;
    settingsMessage = null;
  }

  async function saveSettings(event) {
    event?.preventDefault();
    if (!selectedGenerationId || savingSettings) {
      return;
    }
    settingsError = null;
    settingsMessage = null;
    savingSettings = true;

    const payload = {
      request_date: editForm.request_date,
      target_balance: toNumber(editForm.target_balance) ?? 0,
      customer_name: editForm.customer_name,
      customer_email: editForm.customer_email,
      target_url: editForm.target_url,
      email_target_url: editForm.email_target_url
    };

    try {
      await apiRequest(`/api/generations/${selectedGenerationId}`, {
        method: "PATCH",
        body: { generation: payload }
      });
      editMode = false;
      settingsMessage = "Settings saved.";
      await loadGeneration(selectedGenerationId);
    } catch (error) {
      settingsError = error?.message || "Could not save settings.";
    } finally {
      savingSettings = false;
    }
  }

  let importStatusPollTimer = null;

  async function importTransactions(event) {
    const file = event.target.files?.[0];
    if (!file || !selectedGenerationId || importingTransactions) return;

    importTransactionError = null;
    importTransactionMessage = null;
    importTransactionIssues = [];
    // CSV still imports, but a spreadsheet round-trip can silently corrupt it —
    // say so at upload time, while the operator still has the file in hand
    importTransactionNotice = /\.csv$/i.test(file.name)
      ? "CSV is being retired. Save the sheet as .xlsx (File → Save as → Excel Workbook) and upload that — a CSV re-saved from a spreadsheet can arrive with its columns glued into one."
      : null;
    importingTransactions = true;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await apiRequest(`/api/generations/${selectedGenerationId}/import_transactions`, {
        method: "POST",
        body: formData
      });

      const importId = result.import_id;
      if (!importId) {
        importTransactionError = "No import ID returned.";
        importingTransactions = false;
        event.target.value = "";
        return;
      }

      importTransactionMessage = "Importing...";
      await pollImportStatus(importId);
    } catch (error) {
      importTransactionError = error?.message || "Could not start import.";
      importingTransactions = false;
    } finally {
      event.target.value = "";
    }
  }

  // The API returns import errors as a JSONB array mixing plain strings,
  // aggregated parse rejections ({ reason, rows, example_lines }) and row-save
  // failures ({ transaction_id, errors }). Render all three as readable lines.
  function formatImportErrors(errors) {
    if (!Array.isArray(errors)) return errors ? [String(errors)] : [];
    return errors.map((entry) => {
      if (typeof entry === "string") return entry;
      if (entry?.reason) {
        const count = entry.rows ? `${entry.rows} row${entry.rows === 1 ? "" : "s"} rejected — ` : "";
        const lines = entry.example_lines?.length
          ? ` (e.g. line ${entry.example_lines.join(", ")})`
          : "";
        return `${count}${entry.reason}${lines}`;
      }
      if (entry?.transaction_id) {
        return `${entry.transaction_id}: ${(entry.errors || []).join(", ")}`;
      }
      return JSON.stringify(entry);
    });
  }

  async function pollImportStatus(importId) {
    for (let i = 0; i < 120; i++) {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      try {
        const result = await apiRequest(`/api/generations/${selectedGenerationId}/import_transactions/${importId}`);

        if (result.status === "completed") {
          importTransactionMessage = `Imported ${result.imported || 0} transactions${result.skipped > 0 ? `, skipped ${result.skipped}` : ""}.`;
          // a partial import still has to report the rows it dropped
          importTransactionIssues = formatImportErrors(result.errors);
          importingTransactions = false;
          await loadGeneration(selectedGenerationId);
          return;
        }

        if (result.status === "failed") {
          const issues = formatImportErrors(result.errors);
          importTransactionError = issues.length ? issues[0] : (result.error || "Import failed.");
          importTransactionIssues = issues.slice(1);
          importTransactionMessage = null;
          importingTransactions = false;
          return;
        }

        importTransactionMessage = `Importing...`;
      } catch (error) {
        importTransactionError = error?.message || "Could not check import status.";
        importTransactionMessage = null;
        importingTransactions = false;
        return;
      }
    }

    importTransactionError = "Import timed out.";
    importTransactionMessage = null;
    importingTransactions = false;
  }
</script>

<h2 class="text-base font-bold text-neutral-800 mb-4">Generations</h2>

{#if selectedGenerationId && loadingSelectedGeneration}
  <p class="text-xs text-neutral-500 mb-3">Loading generation details…</p>
{/if}

{#if selectedGenerationId}
  <button type="button" class="btn-secondary mb-4" on:click={onClose}>
    ← Back to generations
  </button>

  {#if loadingSelectedGeneration}
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start animate-pulse">
      <div class="lg:col-span-3 min-w-0 space-y-5">
        <div class="bg-white border border-neutral-200 rounded-lg p-4 space-y-4">
          <div class="flex justify-between items-start gap-4">
            <div class="h-5 bg-neutral-200 rounded-sm w-1/3"></div>
            <div class="h-5 bg-neutral-200 rounded-full w-20"></div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2">
            {#each Array(6) as _}
              <div class="space-y-1">
                <div class="h-3 bg-neutral-200 rounded-sm w-1/2"></div>
                <div class="h-4 bg-neutral-200 rounded-sm w-3/4"></div>
              </div>
            {/each}
          </div>
        </div>
        <div class="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
          <div class="h-4 bg-neutral-200 rounded-sm w-24"></div>
          <div class="h-10 bg-neutral-200 rounded-md w-full"></div>
        </div>
      </div>
      <div class="lg:col-span-1 space-y-5">
        <div class="bg-white border border-neutral-200 rounded-lg p-4 space-y-4">
          <div class="h-4 bg-neutral-200 rounded-sm w-20"></div>
          <div class="space-y-2">
            <div class="h-12 bg-neutral-200 rounded-md w-full"></div>
            <div class="h-12 bg-neutral-200 rounded-md w-full"></div>
          </div>
        </div>
      </div>
    </div>
  {:else if selectedGenerationError}
    <div class="bg-white border border-danger-border/20 rounded-xl p-8 text-center max-w-lg mx-auto my-6 space-y-4 shadow-sm" role="alert">
      <div class="mx-auto w-12 h-12 rounded-full bg-danger-bg flex items-center justify-center text-danger-text">
        <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
      </div>
      <div class="space-y-1">
        <h4 class="text-sm font-bold text-neutral-900">Failed to load generation details</h4>
        <p class="text-xs text-neutral-500 max-w-xs mx-auto">{selectedGenerationError}</p>
      </div>
      <button type="button" class="btn-secondary py-1 text-xs" on:click={() => loadGeneration(selectedGenerationId)}>
        Retry loading
      </button>
    </div>
  {:else if selectedGeneration}
    {@const rows = flattenRows(selectedGeneration)}
    {@const spendingRows = generationSpendingRows(rows)}
    <div class="gen-shell">
      <!-- ============ TOP BAR: title + meta on the left, triggers on the right ============ -->
      <div class="gen-shell__top">
        <article class="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div class="min-w-0">
              <div class="flex items-center gap-3 mb-1 flex-wrap">
                <h3 class="text-sm font-bold text-neutral-900">{selectedGeneration.name}</h3>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider border {generationStatusClass(selectedGeneration.status)}">
                  {formatGenerationStatus(selectedGeneration.status)}
                </span>
                {#if selectedGeneration.artifacts_stale}
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.62rem] font-bold uppercase tracking-wider bg-warning-bg text-warning-text border border-warning-border/30"
                    data-testid="artifacts-stale-badge"
                    title="Rendered pages/emails reflect a rolled-back completion — they regenerate on the next completion"
                  >⟳ artifacts stale</span>
                {/if}
                <span class="text-xs text-neutral-500">
                  <span class="font-semibold text-neutral-700">Target balance:</span> {formatNumber(selectedGeneration.target_balance)}
                </span>
              </div>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-neutral-500">
                <p><span class="font-semibold text-neutral-700">Project:</span> {selectedGeneration.project_name || "(not set)"}</p>
                <p><span class="font-semibold text-neutral-700">Category:</span> {selectedGeneration.project_category || "—"}</p>
                <p><span class="font-semibold text-neutral-700">Request date:</span> {formatDateOnly(selectedGeneration.request_date)}</p>
                <p><span class="font-semibold text-neutral-700">Created:</span> {formatDate(selectedGeneration.created_at)}</p>
                <p><span class="font-semibold text-neutral-700">ID:</span> #{selectedGeneration.id}</p>
              </div>
            </div>

            <!-- Trigger buttons relocated here; the forms/controls still expand in the CENTER column. -->
            <div class="flex items-center gap-2 flex-wrap justify-end shrink-0">
              {#if settingsMessage}
                <span class="text-xs font-semibold text-success-text">{settingsMessage}</span>
              {/if}
              {#if rerenderForId === selectedGeneration.id && rerenderError}
                <span class="text-xs font-semibold text-danger-text" role="alert" data-testid="rerender-error">{rerenderError}</span>
              {:else if rerenderForId === selectedGeneration.id && rerenderMessage}
                <span class="text-xs font-semibold text-neutral-600" data-testid="rerender-status">{rerenderMessage}</span>
              {/if}
              {#if selectedGeneration.status === "draft"}
                <button type="button" class="btn-secondary py-1 text-xs" on:click={() => (editMode ? cancelEdit() : startEdit())}>
                  {editMode ? "Cancel" : "Edit settings"}
                </button>
              {/if}
              {#if selectedGeneration.status === "completed"}
                <button
                  type="button"
                  class="btn-secondary py-1 px-2.5 text-xs"
                  data-testid="unconfirm-btn"
                  disabled={unconfirming}
                  title="Roll back the completion and reopen the agent run to rework — the rows are kept"
                  on:click={unconfirmGeneration}
                >
                  {unconfirming ? "Unconfirming…" : "Unconfirm"}
                </button>
                <button
                  type="button"
                  class="btn-secondary py-1 px-2.5 text-xs"
                  data-testid="rerender-artifacts-btn"
                  disabled={rerenderingArtifacts && rerenderForId === selectedGeneration.id}
                  title={rerenderTitle(selectedGeneration)}
                  on:click={rerenderArtifacts}
                >
                  {rerenderingArtifacts && rerenderForId === selectedGeneration.id ? "Re-rendering…" : "Re-render artifacts"}
                </button>
              {/if}
              {#if canResetGeneration(selectedGeneration)}
                <button
                  type="button"
                  class="btn-danger py-1 px-2.5 text-xs bg-danger-bg text-danger-text border border-danger-border/20"
                  disabled={resetting}
                  on:click={resetGeneratedRows}
                >
                  {resetting ? "Resetting..." : "Reset generated rows"}
                </button>
              {/if}
            </div>
          </div>

        </article>
      </div>

      <!-- ============ LEFT RAIL: run-history archive (Slice 2) ============ -->
      <aside class="gen-shell__left {runsExpanded ? 'gen-runs--expanded' : 'gen-runs--collapsed'}" data-testid="runs-rail">
        {#if runsExpanded}
        <div class="bg-neutral-50 border border-neutral-200 rounded-lg p-3 space-y-2">
          <div class="flex items-center justify-between gap-2">
            <span class="flex items-center gap-1.5">
              <button
                type="button"
                class="group inline-flex items-center justify-center w-5 h-5 rounded border border-neutral-300 bg-white hover:border-brand-400 transition-colors"
                data-testid="runs-toggle"
                aria-expanded="true"
                title="Collapse runs"
                on:click={() => { runsExpanded = false; savePanelPrefs(); }}
              >
                <span class="text-neutral-600 group-hover:text-brand-600 text-[0.7rem] leading-none">◀</span>
              </button>
              <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Runs</h4>
            </span>
            {#if runs.length > 0 && showArchiveView}
              <!-- Escape the archive back to the live panel so a new run can be
                   started even when the default selection is a past run. -->
              <button
                type="button"
                class="text-[0.62rem] font-semibold text-brand-600 hover:text-brand-700"
                data-testid="run-history-new"
                on:click={() => selectRun(panelRunId)}
              >
                {panelRunId ? "Current run" : "+ New run"}
              </button>
            {/if}
          </div>
          {#if runs.length === 0}
            <div class="rounded-md border border-dashed border-neutral-300 bg-white px-2.5 py-2">
              <p class="text-[0.7rem] font-semibold text-neutral-500">No runs yet</p>
              <p class="text-[0.66rem] text-neutral-400 mt-0.5">Start the agent to create the first run.</p>
            </div>
          {:else}
            <ul class="space-y-1.5">
              {#each runs as run (run.id)}
                {@const active = RUN_ACTIVE_STATUSES.has(run.status)}
                {@const selected = run.id === effectiveRunId}
                <li>
                  <button
                    type="button"
                    class="w-full text-left rounded-md border px-2.5 py-2 transition-colors {selected
                      ? 'border-brand-500 bg-white shadow-2xs'
                      : 'border-neutral-200 bg-white hover:border-brand-300'} {active ? 'ring-1 ring-brand-200' : ''}"
                    data-testid="run-history-entry"
                    data-run-id={run.id}
                    aria-current={selected ? "true" : "false"}
                    on:click={() => selectRun(run.id)}
                  >
                    <div class="flex items-center justify-between gap-2 mb-1">
                      <span class="text-[0.7rem] font-bold text-neutral-800 flex items-center gap-1">
                        {#if run.status === "paused"}
                          <!-- ● needs-attention dot: a paused run has a pending HITL gate. -->
                          <span class="text-warning-text" data-testid="run-history-attention" title="Awaiting approval">●</span>
                        {/if}
                        {runLabel(run)}
                      </span>
                      <span
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-wider border {runStatusBadgeClass(run.status)}"
                        data-testid="run-history-status"
                      >
                        {run.status}
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-2 text-[0.62rem] text-neutral-500">
                      <span>{relativeTime(run.created_at)}</span>
                      {#if toNumber(run.cost)}
                        <!-- cost defaults to "0.0" (never null) for a just-started
                             run; only show the chip once it rounds to a nonzero. -->
                        <span class="font-mono" title="Cost">${formatNumber(run.cost, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                      {/if}
                    </div>
                    {#if active}
                      <span class="mt-1 inline-flex items-center gap-1 text-[0.58rem] font-bold uppercase tracking-wider text-brand-600" data-testid="run-history-live">
                        <span class="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse"></span> Live
                      </span>
                    {/if}
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
        {:else}
          <!-- Collapsed rail: a thin vertical "Runs" strip. A live/attention dot
               keeps a running or pending-gate run visible even when collapsed. -->
          <button
            type="button"
            class="group relative lg:sticky lg:top-4 h-40 w-8 flex flex-col items-center justify-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg hover:border-brand-500 transition-colors"
            data-testid="runs-toggle"
            aria-expanded="false"
            title="Show runs"
            on:click={() => { runsExpanded = true; savePanelPrefs(); }}
          >
            {#if runs.some((r) => r.status === "paused")}
              <span class="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full bg-warning-text text-white text-[0.5rem] font-bold leading-none" data-testid="runs-rail-attention" title="A run is awaiting approval">●</span>
            {:else if runs.some((r) => RUN_ACTIVE_STATUSES.has(r.status))}
              <span class="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full bg-brand-500 animate-pulse" data-testid="runs-rail-live" title="A run is live"></span>
            {/if}
            <span class="text-neutral-600 group-hover:text-brand-600 text-sm leading-none">▶</span>
            <span class="gen-runs__rail-label text-xs font-bold text-neutral-700">Runs</span>
          </button>
        {/if}
      </aside>

      <!-- ============ CENTER: the agent run panel (primary column) + expanding forms ============ -->
      <div class="gen-shell__center space-y-5">
        {#if showArchiveView && selectedRun}
          <RunArchiveView run={selectedRun} />
        {:else}
          <AgentRunPanel
            generationId={selectedGeneration.id}
            generationStatus={selectedGeneration.status}
            onStatusChange={handleRunStatusChange}
            onRowsChanged={handleRowsChanged}
            onFlaggedRulesChange={handleFlaggedRulesChange}
          />
        {/if}

        {#if editMode}
          <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-3.5 max-w-lg space-y-3.5" on:submit={saveSettings}>
            <h4 class="text-xs font-bold text-neutral-800 tracking-wider">Settings</h4>

            <div class="form-group">
              <label class="form-label" for="edit-request-date">Request date</label>
              <input id="edit-request-date" class="form-input" type="date" value={editForm.request_date} on:input={(event) => (editForm.request_date = event.target.value)} />
              <p class="text-xs text-neutral-500 mt-1">The date this statement is issued for — it labels the artifact. Metadata only; it does not affect balances or transaction dates.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-target-bal">Target balance <span class="text-neutral-400 font-normal">· guardrail</span></label>
              <input id="edit-target-bal" class="form-input" type="number" min="0" value={editForm.target_balance} on:input={(event) => (editForm.target_balance = event.target.value)} />
              <p class="text-xs text-neutral-500 mt-1">Upper-bound guardrail enforced at completion: the final balance must not exceed this value. Expressed in the project's currency units (e.g. BOWS).</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-customer-name">Customer name</label>
              <input id="edit-customer-name" class="form-input" type="text" value={editForm.customer_name} on:input={(event) => (editForm.customer_name = event.target.value)} data-testid="edit-customer-name" />
              <p class="text-xs text-neutral-500 mt-1">Fallback display name for confirmation emails when the imported row carries no cardholder name. Optional.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-customer-email">Customer email</label>
              <input id="edit-customer-email" class="form-input" type="email" placeholder="e.g. user@example.com" value={editForm.customer_email} on:input={(event) => (editForm.customer_email = event.target.value)} data-testid="edit-customer-email" />
              <p class="text-xs text-neutral-500 mt-1">The customer this report is about. Fills the <code>user_email</code> of imported rows whose cell is empty (some back-office exports omit it) and of rows the agent posts. An address present in the file always wins. Optional — but then the import needs <code>user_email</code> in every row.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-target-url">Target URL <span class="text-neutral-400 font-normal">· optional</span></label>
              <input id="edit-target-url" class="form-input" type="text" placeholder="e.g. https://mybank.example" value={editForm.target_url} on:input={(event) => (editForm.target_url = event.target.value)} data-testid="settings-target-url" />
              <p class="text-xs text-neutral-500 mt-1">The render origin the report pages appear to be served from. Inherited from the template on create; editable per generation.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-email-target-url">Email render URL <span class="text-neutral-400 font-normal">· optional</span></label>
              <input id="edit-email-target-url" class="form-input" type="text" placeholder="e.g. https://mail.mybank.example" value={editForm.email_target_url} on:input={(event) => (editForm.email_target_url = event.target.value)} data-testid="settings-email-target-url" />
              <p class="text-xs text-neutral-500 mt-1">A separate render origin for the confirmation-email artifacts (the admin log pages keep using Target URL). Falls back to Target URL when blank.</p>
            </div>

            <fieldset disabled class="form-group opacity-60 cursor-not-allowed">
              <span class="form-label">Data source <span class="text-neutral-400 font-normal">· not available yet</span></span>
              <div class="bg-white border border-neutral-300 rounded-md p-2.5 space-y-1.5">
                <label class="flex items-center gap-2 text-xs text-neutral-500">
                  <input type="checkbox" disabled class="h-4 w-4" />
                  <span>Transaction IDs / links</span>
                </label>
                <p class="text-xs text-neutral-500">Pulling rows from Backoffice by transaction IDs or links isn't available yet (no radio needed — plain numeric IDs and URLs are auto-detected). To add data now, use the <span class="font-semibold">Import transactions</span> form (CSV / XLSX) in the Transactions block below.</p>
              </div>
            </fieldset>

            <div class="flex justify-end">
              <button type="submit" class="btn-primary py-1 text-xs" disabled={savingSettings}>
                {savingSettings ? "Saving..." : "Save settings"}
              </button>
            </div>
          </form>
          {#if settingsError}
            <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-xs" role="alert">{settingsError}</p>
          {/if}
        {/if}

        <section class="space-y-2">
          <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Pages</h4>
          {#if screenshotError}
            <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-xs" role="alert" data-testid="screenshot-error">{screenshotError}</p>
          {/if}
          {#if !selectedGeneration.generation_pages || selectedGeneration.generation_pages.length === 0}
            <p class="text-neutral-500 text-xs">No pages available yet.</p>
          {:else}
            <ul class="space-y-2">
              {#each selectedGeneration.generation_pages as page}
                <li class="bg-white border border-neutral-200 rounded-lg p-3 shadow-2xs">
                  <div class="flex justify-between items-center gap-3 flex-wrap">
                    <span class="text-xs font-semibold text-neutral-800">
                      Page #{page.page_number || "?"} — {page.generation_rows?.length || 0} rows
                      {#if page.has_errors}
                        <span class="text-xs text-danger-text font-bold ml-1">render error</span>
                      {/if}
                    </span>
                    {#if page.rendered_html}
                      <span class="flex gap-2 items-center text-xs flex-wrap">
                        <a
                          class="btn-secondary py-1 px-2 text-[0.72rem]"
                          data-testid="download-page"
                          href={pageDownloadUrl(page)}
                          download={`page_${page.page_number}.html`}
                        >Download HTML</a>
                        <button
                          type="button"
                          class="btn-secondary py-1 px-2 text-[0.72rem]"
                          data-testid="toggle-preview"
                          on:click={() => togglePreview(page.id)}
                        >
                          {openPreviews[page.id] ? "Hide preview" : "Preview"}
                        </button>
                        {#if page.screenshot_status === "ready"}
                          <a
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="download-png"
                            href={pngDownloadUrl(page)}
                          >Download PNG</a>
                          <button
                            type="button"
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="regenerate-png"
                            disabled={isScreenshotInFlight(page)}
                            on:click={() => generateScreenshot(page.id)}
                          >Re-generate</button>
                        {:else if isScreenshotInFlight(page)}
                          <span class="text-neutral-500 text-[0.72rem]" data-testid="png-rendering">Rendering PNG…</span>
                        {:else}
                          <button
                            type="button"
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="generate-png"
                            on:click={() => generateScreenshot(page.id)}
                          >Generate PNG</button>
                          {#if page.screenshot_status === "failed"}
                            <span class="text-xs text-danger-text font-bold" data-testid="png-error">
                              {page.screenshot_error || "screenshot failed"}
                            </span>
                          {/if}
                        {/if}
                      </span>
                    {:else}
                      <span class="text-neutral-400 text-[0.72rem]">Not rendered yet</span>
                    {/if}
                  </div>
                  {#if page.rendered_html && openPreviews[page.id]}
                    <iframe
                      class="w-full h-[320px] mt-2 border border-neutral-300 rounded bg-white shadow-inner"
                      data-testid="page-preview"
                      title={`Page ${page.page_number || "?"} preview`}
                      srcdoc={page.rendered_html}
                      sandbox="allow-same-origin"
                    ></iframe>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </section>

        <section class="space-y-2" data-testid="email-artifacts">
          <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Emails</h4>
          {#if emailScreenshotError}
            <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-xs" role="alert" data-testid="email-screenshot-error">{emailScreenshotError}</p>
          {/if}
          {#if !selectedGeneration.email_artifacts || selectedGeneration.email_artifacts.length === 0}
            <p class="text-neutral-500 text-xs">No emails available yet.</p>
          {:else}
            <ul class="space-y-2">
              {#each selectedGeneration.email_artifacts as email}
                <li class="bg-white border border-neutral-200 rounded-lg p-3 shadow-2xs" data-testid="email-artifact">
                  <div class="flex justify-between items-center gap-3 flex-wrap">
                    <span class="text-xs text-neutral-800 flex items-baseline gap-2 flex-wrap min-w-0">
                      {#if rowIsTopUp(email)}
                        <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-info-bg text-info-text border border-info-border/20 shrink-0">Top-up</span>
                      {/if}
                      <span class="font-semibold whitespace-nowrap" data-testid="email-date">{emailLabel(email)}</span>
                      <span class="font-mono font-semibold text-neutral-900 whitespace-nowrap" data-testid="email-amount">{emailAmount(email)}</span>
                      <span class="text-neutral-600 truncate" data-testid="email-description">{emailDescription(email)}</span>
                      {#if email.email_address}
                        <span class="text-neutral-400 ml-1 truncate">{email.email_address}</span>
                      {/if}
                    </span>
                    {#if email.rendered_html}
                      <span class="flex gap-2 items-center text-xs flex-wrap">
                        <a
                          class="btn-secondary py-1 px-2 text-[0.72rem]"
                          data-testid="download-email"
                          href={emailDownloadUrl(email)}
                          download={`email_${email.id}.html`}
                        >Download HTML</a>
                        <button
                          type="button"
                          class="btn-secondary py-1 px-2 text-[0.72rem]"
                          data-testid="toggle-email-preview"
                          on:click={() => toggleEmailPreview(email.id)}
                        >
                          {openEmailPreviews[email.id] ? "Hide preview" : "Preview"}
                        </button>
                        {#if email.screenshot_status === "ready"}
                          <a
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="download-email-png"
                            href={emailPngDownloadUrl(email)}
                          >Download PNG</a>
                          <button
                            type="button"
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="regenerate-email-png"
                            disabled={isEmailScreenshotInFlight(email)}
                            on:click={() => generateEmailScreenshot(email.id)}
                          >Re-generate</button>
                        {:else if isEmailScreenshotInFlight(email)}
                          <span class="text-neutral-500 text-[0.72rem]" data-testid="email-png-rendering">Rendering PNG…</span>
                        {:else}
                          <button
                            type="button"
                            class="btn-secondary py-1 px-2 text-[0.72rem]"
                            data-testid="generate-email-png"
                            on:click={() => generateEmailScreenshot(email.id)}
                          >Generate PNG</button>
                          {#if email.screenshot_status === "failed"}
                            <span class="text-xs text-danger-text font-bold" data-testid="email-png-error">
                              {email.screenshot_error || "screenshot failed"}
                            </span>
                          {/if}
                        {/if}
                      </span>
                    {:else}
                      <span class="text-neutral-400 text-[0.72rem]">Not rendered yet</span>
                    {/if}
                  </div>
                  {#if email.rendered_html && openEmailPreviews[email.id]}
                    <iframe
                      class="w-full h-[320px] mt-2 border border-neutral-300 rounded bg-white shadow-inner"
                      data-testid="email-preview"
                      title={`Email ${emailLabel(email)} preview`}
                      srcdoc={email.rendered_html}
                      sandbox="allow-same-origin"
                    ></iframe>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </section>
      </div>

      <!-- ============ RIGHT: Rules editor in a collapsible accordion ============ -->
      <aside class="gen-shell__right {rulesExpanded ? 'gen-rules--expanded' : 'gen-rules--collapsed'}" data-testid="rules-accordion">
        {#if rulesExpanded}
          <div class="bg-white border border-neutral-200 rounded-lg overflow-hidden lg:sticky lg:top-4">
            <button
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-left"
              data-testid="rules-toggle"
              aria-expanded="true"
              on:click={() => { rulesExpanded = false; savePanelPrefs(); }}
            >
              <span class="text-xs font-bold text-neutral-700 tracking-wider">Rules</span>
              <span class="inline-flex items-center justify-center w-5 h-5 rounded border border-neutral-300 bg-white text-neutral-600 text-[0.7rem] leading-none">▶</span>
            </button>
            <div class="p-3">
              <RuleEditor generationId={selectedGeneration.id} {isAdmin} {flaggedRuleIds} {onEditRules} />
            </div>
          </div>
        {:else}
          <button
            type="button"
            class="relative lg:sticky lg:top-4 h-40 w-8 flex flex-col items-center justify-center gap-2 bg-neutral-50 border rounded-lg transition-colors {flaggedRuleIds.length > 0 ? 'border-danger-border/50 ring-2 ring-danger-border/30 bg-danger-bg/30' : 'border-neutral-200 hover:border-brand-500'}"
            data-testid="rules-toggle"
            aria-expanded="false"
            title={flaggedRuleIds.length > 0 ? `${flaggedRuleIds.length} rule(s) blocking the agent — show rules` : "Show rules"}
            on:click={() => { rulesExpanded = true; savePanelPrefs(); }}
          >
            {#if flaggedRuleIds.length > 0}
              <!-- Blocked-rules indicator on the collapsed rail so a failing rule
                   is never hidden behind a collapsed panel. -->
              <span
                class="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-danger-text text-white text-[0.55rem] font-bold leading-none"
                data-testid="rules-rail-flagged"
              >{flaggedRuleIds.length}</span>
            {/if}
            <span class="text-neutral-600 text-sm leading-none">◀</span>
            <span class="gen-rules__rail-label text-xs font-bold {flaggedRuleIds.length > 0 ? 'text-danger-text' : 'text-neutral-700'}">Rules</span>
          </button>
        {/if}
      </aside>

      <!-- ============ BOTTOM: transactions/rows as an in-flow, full-width collapsible block ============ -->
      <section class="gen-shell__bottom" data-testid="transactions-block">
        <div class="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <button
            type="button"
            class="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-left"
            data-testid="transactions-toggle"
            aria-expanded={transactionsExpanded}
            on:click={() => { transactionsExpanded = !transactionsExpanded; savePanelPrefs(); }}
          >
            <span class="flex items-center gap-2">
              <span class="text-xs font-bold text-neutral-700 tracking-wider">Transactions</span>
              <span class="inline-flex items-center justify-center w-5 h-5 rounded border border-neutral-300 bg-white text-neutral-600 text-[0.7rem] leading-none">{transactionsExpanded ? "▼" : "▶"}</span>
            </span>
            <span class="text-xs text-neutral-500">
              {#if rows.length === 0}
                No spending rows yet
              {:else}
                <span class="font-semibold text-neutral-700">{rows.length}</span> rows ·
                <span class="font-semibold text-neutral-700">{formatNumber(sumSpendingAmount(spendingRows))}</span> spent ·
                balance <span class="font-semibold text-neutral-700">{#if rows[rows.length - 1]}{formatNumber(rows[rows.length - 1].balance)}{:else}—{/if}</span>
              {/if}
            </span>
          </button>

          {#if transactionsExpanded}
          <div class="p-4 space-y-3">
          {#if resetMessage}
            <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-2.5 py-1.5 text-xs" role="status">{resetMessage}</p>
          {/if}
          {#if resetError}
            <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs" role="alert">{resetError}</p>
          {/if}

          {#if rows.length === 0}
            <div class="bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-8 text-center my-2 space-y-2">
              <p class="text-neutral-500 text-xs font-medium">No spending rows yet for this generation.</p>
              <p class="text-[0.7rem] text-neutral-400 max-w-xs mx-auto">Use the MCP client tool <code>post_transaction</code>, or import a file below, to create/append spending confirmation rows.</p>
            </div>
          {:else}
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-xs" role="status">
              <p><span class="font-bold text-neutral-700">Total rows:</span> {rows.length}</p>
              <p><span class="font-bold text-neutral-700">Spending rows:</span> {spendingRows.length}</p>
              <p><span class="font-bold text-neutral-700">Total spent:</span> {formatNumber(sumSpendingAmount(spendingRows))}</p>
              <p>
                <span class="font-bold text-neutral-700">Final balance:</span>
                {#if rows[rows.length - 1]}
                  {formatNumber(rows[rows.length - 1].balance)}
                {:else}
                  —
                {/if}
              </p>
            </div>

            <div class="overflow-x-auto border border-neutral-200 rounded-lg" bind:this={txTableEl}>
              <table class="w-full border-collapse text-xs text-left min-w-[900px]">
                <thead>
                  <tr class="bg-neutral-50 border-b border-neutral-200">
                    <th class="p-2 font-bold text-neutral-700">Order</th>
                    <th class="p-2 font-bold text-neutral-700">Type</th>
                    <th class="p-2 font-bold text-neutral-700">Category</th>
                    <th class="p-2 font-bold text-neutral-700">Date/time</th>
                    <th class="p-2 font-bold text-neutral-700">Description</th>
                    <th class="p-2 font-bold text-neutral-700">Amount</th>
                    <th class="p-2 font-bold text-neutral-700">Amount EUR</th>
                    <th class="p-2 font-bold text-neutral-700">Balance</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-100">
                  <!-- Newest transactions first (like bank/statement views): a
                       display-only reverse of a COPY — `rows` stays chronological
                       for the summary/totals above. The "#" column still shows the
                       canonical row_order, so a just-posted row lands near the top
                       (visible without scrolling) and the flash marks it. -->
                  {#each [ ...rows ].reverse() as row (row.id)}
                    {@const flash = rowFlash.get(row.id)}
                    <tr
                      class="{rowTypeBadgeClass(row)} {flash === 'add' ? 'row-flash-add' : flash === 'remove' ? 'row-flash-remove' : ''}"
                      data-row-id={row.id}
                    >
                      <td class="p-2 font-medium">{row.row_order || "—"}</td>
                      <td class="p-2">
                        {#if rowIsTopUp(row)}
                          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-info-bg text-info-text border border-info-border/20">Top-up</span>
                        {:else if row.is_real === true}
                          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-success-bg text-success-text border border-success-border/20">Real spending</span>
                        {:else}
                          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-200 text-neutral-700 border border-neutral-300">MCP spending</span>
                        {/if}
                      </td>
                      <td class="p-2 text-neutral-600" data-testid="row-category">{row.category || "—"}</td>
                      <td class="p-2 whitespace-nowrap text-neutral-600">{formatDate(row.date_time)}</td>
                      <td class="p-2 text-neutral-900">{row.description || "—"}</td>
                      <td class="p-2 font-mono text-neutral-900">{formatNumber(row.amount)}</td>
                      <td class="p-2 font-mono text-neutral-900">{formatNumber(row.amount_eur)}</td>
                      <td class="p-2 font-mono font-semibold text-neutral-900">{formatNumber(row.balance)}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}

          {#if selectedGeneration.status === "draft" && isAdmin}
            <div class="border-t border-neutral-200 pt-3 mt-3 space-y-2">
              <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Import spending / transactions from a file</h4>

              <div class="flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  data-testid="import-transactions-input"
                  on:change={importTransactions}
                  bind:this={importFileInput}
                  disabled={importingTransactions}
                  class="text-xs text-neutral-600 file:mr-2 file:rounded-md file:border file:border-neutral-300 file:bg-white file:px-2.5 file:py-1 file:text-xs file:font-semibold file:text-neutral-700 hover:file:bg-neutral-50 file:cursor-pointer disabled:opacity-50"
                />
                {#if importTransactionMessage}
                  <span class="text-xs font-semibold {importingTransactions ? 'text-neutral-500 animate-pulse' : 'text-success-text'}">{importTransactionMessage}</span>
                {/if}
                {#if importTransactionError}
                  <p class="text-xs text-danger-text font-semibold" data-testid="import-error">{importTransactionError}</p>
                {/if}
              </div>

              {#if importTransactionNotice}
                <p class="text-xs text-warning-text bg-warning-bg border border-warning-border/30 rounded-md px-2 py-1.5" data-testid="import-notice">{importTransactionNotice}</p>
              {/if}

              {#if importTransactionIssues.length}
                <ul class="text-xs text-danger-text list-disc pl-5 space-y-0.5" data-testid="import-issues">
                  {#each importTransactionIssues as issue}
                    <li>{issue}</li>
                  {/each}
                </ul>
              {/if}

              <div class="overflow-x-auto border border-neutral-200 rounded-lg">
                <table class="w-full border-collapse text-xs text-left min-w-[900px]">
                  <thead>
                    <tr class="bg-neutral-50 border-b border-neutral-200">
                      <th class="p-2 font-bold text-neutral-700">Column</th>
                      <th class="p-2 font-bold text-neutral-700">Required</th>
                      <th class="p-2 font-bold text-neutral-700">Meaning</th>
                      <th class="p-2 font-bold text-neutral-700">Example</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-neutral-100">
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">uuid</td>
                      <td class="p-2 text-neutral-600">Yes</td>
                      <td class="p-2 text-neutral-900">Unique transaction id (UUID). A row whose <code>uuid</code> was already imported is skipped on re-import.</td>
                      <td class="p-2 font-mono text-neutral-900">a1b2c3d4-e5f6-7890-abcd-ef1234567890</td>
                    </tr>
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">user_email</td>
                      <td class="p-2 text-neutral-600">Yes, unless Customer email is set</td>
                      <td class="p-2 text-neutral-900">Payer email — saved as the row's <code>email_address</code>. An empty cell falls back to the generation's <strong>Customer email</strong> setting; with neither, the row is rejected.</td>
                      <td class="p-2 font-mono text-neutral-900">user@example.com</td>
                    </tr>
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">amount</td>
                      <td class="p-2 text-neutral-600">Yes</td>
                      <td class="p-2 text-neutral-900">Positive number — the amount in <code>currency</code>. A number cell is used as-is; as text both <code>10.50</code> and <code>10,50</code> are accepted. Converted to the project's currency (BOWS) by the exchange rate; the EUR value is kept as <code>amount_eur</code>.</td>
                      <td class="p-2 font-mono text-neutral-900">10.00</td>
                    </tr>
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">currency</td>
                      <td class="p-2 text-neutral-600">No (default EUR)</td>
                      <td class="p-2 text-neutral-900">Only <code>EUR</code> is accepted. Blank is treated as EUR; any other non-EUR value rejects the row.</td>
                      <td class="p-2 font-mono text-neutral-900">EUR</td>
                    </tr>
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">paid_at</td>
                      <td class="p-2 text-neutral-600">Yes</td>
                      <td class="p-2 text-neutral-900">Payment date/time → the row's <code>date_time</code>. In XLSX a real date cell is used as-is. As text, write <code>YYYY-MM-DD</code>: a slash date such as <code>03/04/2025</code> is rejected rather than guessed, because its day/month order depends on the locale that wrote it.</td>
                      <td class="p-2 font-mono text-neutral-900">2025-06-01 10:00:00</td>
                    </tr>
                    <tr>
                      <td class="p-2 font-mono text-neutral-900">chargeback_date</td>
                      <td class="p-2 text-neutral-600">No</td>
                      <td class="p-2 text-neutral-900">If set, the transaction is flagged as a chargeback (<code>original_data.chargeback = true</code>). Column alias: <code>chargebacked_at</code>. Blank means no chargeback.</td>
                      <td class="p-2 font-mono text-neutral-900">(blank)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <a
                  href="/transactions-example.xlsx"
                  download="transactions-example.xlsx"
                  class="text-xs text-brand-600 underline hover:text-brand-700"
                  data-testid="import-transactions-example-xlsx"
                >
                  Download example XLSX
                </a>
                <a
                  href="/transactions-example.csv"
                  download="transactions-example.csv"
                  class="text-xs text-neutral-500 underline hover:text-neutral-700"
                  data-testid="import-transactions-example"
                >
                  example CSV
                </a>
                <p class="text-[0.68rem] text-neutral-500">Formats: <strong>XLSX — recommended</strong>; CSV still works but is being retired (a spreadsheet can corrupt it on re-save).</p>
              </div>
            </div>
          {/if}
          </div>
          {/if}
        </div>
      </section>
    </div>
  {/if}
{:else}
  <div class="flex justify-between items-center gap-3 mb-4">
    <span></span>
    <button type="button" class="btn-secondary py-1 text-xs" on:click={() => (showCreateForm = !showCreateForm)}>
      {showCreateForm ? "Cancel" : "+ New generation"}
    </button>
  </div>

  {#if showCreateForm}
    <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6 space-y-4 w-full" on:submit={createGeneration}>
      <h3 class="text-xs font-bold text-neutral-800 tracking-wider">New Generation</h3>
      
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="form-group">
          <label class="form-label" for="gen-name">Name</label>
          <input
            id="gen-name"
            class="form-input"
            required
            data-testid="new-gen-name"
            bind:value={generationForm.name}
            placeholder="e.g. BOWS_june_28"
          />
        </div>
        
        <div class="form-group">
          <label class="form-label" for="new-gen-template">Template</label>
          {#if loadingTemplates}
            <span class="text-xs text-neutral-500">Loading templates…</span>
          {:else}
            <select
              required
              id="new-gen-template"
              data-testid="new-gen-template"
              class="form-input"
              bind:value={generationForm.project_template_id}
            >
              <option value="" disabled>Pick a template…</option>
              {#each templates as template}
                <option value={template.id}>{template.name}{template.project_name ? ` — ${template.project_name}` : ""}</option>
              {/each}
            </select>
          {/if}
          <p class="text-xs text-neutral-500 mt-1">The generation's project — its catalog, currency and guardrails — comes from the template you pick here (each template belongs to one project). There's no separate project selector.</p>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label" for="new-gen-target-bal">Target balance <span class="font-normal normal-case text-neutral-400">(optional)</span></label>
        <input
          id="new-gen-target-bal"
          class="form-input sm:max-w-xs"
          type="number"
          min="0"
          step="any"
          data-testid="new-gen-target-balance"
          bind:value={generationForm.target_balance}
          placeholder="e.g. 20"
        />
        <p class="text-xs text-neutral-500 mt-1">The balance the run should spend down to. <strong>Leave blank for no target</strong> — but then a run can complete with almost no spending. Editable later in settings.</p>
      </div>

      <p class="text-xs text-neutral-500">Name and template are required; request date and data sources are configured afterwards in the draft's settings.</p>

      <div class="flex justify-end pt-2 border-t border-neutral-200">
        <button type="submit" class="btn-primary" disabled={creatingGeneration}>
          {creatingGeneration ? "Creating..." : "Create generation"}
        </button>
      </div>
    </form>
    {#if createGenerationError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs mb-4" role="alert">{createGenerationError}</p>
    {/if}
  {/if}

  {#if loadingGenerations}
    <div class="space-y-3">
      {#each Array(3) as _}
        <div class="w-full bg-white border border-neutral-200 rounded-lg p-3.5 space-y-3 animate-pulse">
          <div class="flex justify-between items-center">
            <div class="h-4 bg-neutral-200 rounded-sm w-1/4"></div>
            <div class="h-4 bg-neutral-200 rounded-full w-16"></div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div class="h-3 bg-neutral-200 rounded-sm w-3/4"></div>
            <div class="h-3 bg-neutral-200 rounded-sm w-2/3"></div>
            <div class="h-3 bg-neutral-200 rounded-sm w-1/2"></div>
            <div class="h-3 bg-neutral-200 rounded-sm w-3/4"></div>
            <div class="h-3 bg-neutral-200 rounded-sm w-1/3"></div>
          </div>
        </div>
      {/each}
    </div>
  {:else if generationsError}
    <div class="bg-white border border-danger-border/20 rounded-xl p-8 text-center max-w-lg mx-auto my-6 space-y-4 shadow-sm" role="alert">
      <div class="mx-auto w-12 h-12 rounded-full bg-danger-bg flex items-center justify-center text-danger-text">
        <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
      </div>
      <div class="space-y-1">
        <h4 class="text-sm font-bold text-neutral-900">Request failed</h4>
        <p class="text-xs text-neutral-500 max-w-xs mx-auto">{generationsError}</p>
      </div>
      <button type="button" class="btn-secondary py-1 text-xs" on:click={loadGenerations}>
        Retry loading
      </button>
    </div>
  {:else if generations.length === 0}
    <div class="bg-white border border-neutral-200 rounded-xl p-8 text-center max-w-lg mx-auto my-6 space-y-4 shadow-sm">
      <div class="mx-auto w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center text-brand-600">
        <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      </div>
      <div class="space-y-1">
        <h4 class="text-sm font-semibold text-neutral-900">No generations yet</h4>
        <p class="text-xs text-neutral-500 max-w-sm mx-auto">Create your first report generation to start managing and exporting transaction spending confirmation documents.</p>
      </div>
      {#if isAdmin}
        <button type="button" class="btn-primary" on:click={() => (showCreateForm = true)}>+ New generation</button>
      {/if}
    </div>
  {:else}
    <section class="grid grid-cols-1 gap-3">
      {#each generations as generation}
        <button type="button" class="generation-card w-full text-left bg-white border border-neutral-200 rounded-lg p-3.5 hover:border-brand-500 hover:shadow-md hover:shadow-brand-500/5 transition-all duration-150 text-neutral-900 group" on:click={() => onOpen(generation.id)}>
          <div class="flex items-center justify-between gap-3 mb-2">
            <h3 class="text-sm font-bold text-neutral-800 group-hover:text-brand-600 transition-colors">{generation.name}</h3>
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider border {generationStatusClass(generation.status)}">
              {formatGenerationStatus(generation.status)}
            </span>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1 text-xs text-neutral-500">
            <p><span class="font-semibold text-neutral-700">Project:</span> {generation.project_name || "(not set)"}</p>
            <p><span class="font-semibold text-neutral-700">Category:</span> {generation.project_category || "—"}</p>
            <p><span class="font-semibold text-neutral-700">Request date:</span> {formatDateOnly(generation.request_date)}</p>
            <p><span class="font-semibold text-neutral-700">Target balance:</span> {formatNumber(generation.target_balance)}</p>
            <p class="col-span-2 md:col-span-1"><span class="font-semibold text-neutral-700">Created:</span> {formatDate(generation.created_at)}</p>
          </div>
        </button>
      {/each}
    </section>
  {/if}
{/if}
